import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import '../../styles/room.css';
import { usePopup } from '../../contexts/PopupContext';
import Loading from '../../components/Loading';
import { QRCodeSVG } from 'qrcode.react';

const maskEmail = (email) => {
  if (!email) return '';
  const [username, domain] = email.split('@');
  const maskedUsername = username.charAt(0) + '*'.repeat(username.length - 2) + username.charAt(username.length - 1);
  return `${maskedUsername}@${domain}`;
};

function Room() {
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreator, setIsCreator] = useState(false);
  const [isRandomizing, setIsRandomizing] = useState(false);
  const [currentRandomName, setCurrentRandomName] = useState('');
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [showWheel, setShowWheel] = useState(false);
  const [spinSpeed, setSpinSpeed] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [participantsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStats, setRoomStats] = useState({
    totalParticipants: 0,
    onlineParticipants: 0,
    averageWaitTime: 0
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState(room?.maxParticipants || 100);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { showPopup } = usePopup();
  const [products, setProducts] = useState(room?.products || []);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [newProduct, setNewProduct] = useState('');
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [productWinners, setProductWinners] = useState({});
  const [spinItems, setSpinItems] = useState([]);
  const [spinPosition, setSpinPosition] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [winners, setWinners] = useState([]);
  const [visibleEmails, setVisibleEmails] = useState({});

  const toggleEmailVisibility = (participantId) => {
    setVisibleEmails(prev => ({
      ...prev,
      [participantId]: !prev[participantId]
    }));
  };

  const fetchRoom = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/rooms/${roomId}`);
      setRoom(response.data);
      setParticipants(response.data.participants || []);
      setIsCompleted(response.data.isCompleted);
      setWinners(response.data.winners || []);
      setIsLocked(response.data.isLocked);
      setLoading(false);
      
      if (response.data.products) {
        setProducts(response.data.products);
      }
      
      if (response.data.productWinners) {
        setProductWinners(response.data.productWinners);
        setCurrentProductIndex(Object.keys(response.data.productWinners).length);
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          const creatorCheck = await axios.get(
            `${process.env.REACT_APP_API_URL}/rooms/${roomId}/check-creator`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setIsCreator(creatorCheck.data.isCreator);
        } catch (err) {
          setIsCreator(false);
        }
      }
    } catch (error) {
      console.error('Error fetching room:', error);
      setLoading(false);
    }
  };

  // Thêm polling để cập nhật danh sách người tham gia
  useEffect(() => {
    fetchRoom(); // Lần đầu fetch

    // Tạo interval để fetch mỗi 3 giây
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/rooms/${roomId}`);
        if (
          response.data.participants.length !== participants.length ||
          response.data.isLocked !== isLocked
        ) {
          setRoom(response.data);
          setParticipants(response.data.participants || []);
          setIsLocked(response.data.isLocked);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [roomId, participants.length, isLocked]);

  const fireConfetti = () => {
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#ff0000', '#00ff00', '#0000ff']
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#ff0000', '#00ff00', '#0000ff']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  // Thêm hàm kiểm tra sản phẩm đã có người thắng chưa
  const hasWinner = (product) => {
    return productWinners[product] && productWinners[product].winner;
  };

  // Tìm index của sản phẩm chưa có người thắng tiếp theo
  const findNextUnwonProduct = () => {
    return products.findIndex(product => !hasWinner(product));
  };

  // Hàm tạo danh sách items để quay
  const generateSpinItems = (participants, count = 50) => {
    const items = [];
    for (let i = 0; i < count; i++) {
      const randomParticipant = participants[Math.floor(Math.random() * participants.length)];
      items.push(randomParticipant);
    }
    return items;
  };

  // Thêm hàm finalizePick
  const finalizePick = async (winner) => {
    try {
      const currentProduct = products[findNextUnwonProduct()];
      const token = localStorage.getItem('token');

      // Gọi API để lưu kết quả
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}/winners`,
        {
          product: currentProduct,
          winner: winner
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Cập nhật state local
      if (response.data.productWinners) {
        setProductWinners(response.data.productWinners);
      }

      // Hiệu ứng confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      // Hiển thị thông báo
      showPopup({
        type: 'success',
        title: 'Chúc mừng! 🎉',
        message: `${maskEmail(winner)} đã trúng ${currentProduct}!`,
        showCancelButton: false,
        onConfirm: () => {}
      });

      // Delay trước khi ẩn wheel
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowWheel(false);
      setIsRandomizing(false);

    } catch (error) {
      console.error('Finalize pick error:', error);
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: 'Không thể lưu kết quả quay thưởng',
        showCancelButton: false,
        onConfirm: () => {}
      });
      setShowWheel(false);
      setIsRandomizing(false);
    }
  };

  // Cập nhật hàm handlePickWinner
  const handlePickWinner = async () => {
    // Kiểm tra phòng đã khóa chưa
    if (!isLocked) {
      showPopup({
        type: 'warning',
        title: 'Phòng chưa khóa',
        message: 'Vui lòng khóa phòng trước khi quay thưởng để tránh xung đột dữ liệu',
        showCancelButton: true,
        confirmText: 'Khóa ngay',
        cancelText: 'Hủy',
        onConfirm: async () => {
          try {
            // Gọi API khóa phòng
            const token = localStorage.getItem('token');
            const response = await axios.put(
              `${process.env.REACT_APP_API_URL}/rooms/${roomId}/toggle-lock`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );

            // Cập nhật state
            setIsLocked(response.data.isLocked);
            
            // Hiển thị thông báo thành công
            showPopup({
              type: 'success',
              title: 'Thành công',
              message: 'Đã khóa phòng. Bạn có thể bắt đầu quay thưởng',
              showCancelButton: false,
              confirmText: 'OK',
              onConfirm: () => startPickWinner()
            });
          } catch (error) {
            showPopup({
              type: 'error',
              title: 'Lỗi',
              message: error.response?.data?.error || 'Có lỗi xảy ra khi khóa phòng',
              showCancelButton: false
            });
          }
        }
      });
      return;
    }

    startPickWinner(); // Gọi hàm quay thưởng nếu phòng đã khóa
  };

  // Tách logic quay thưởng ra thành hàm riêng
  const startPickWinner = async () => {
    if (!room || !products.length) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: 'Vui lòng thêm sản phẩm trước khi quay thưởng',
        showCancelButton: false,
        onConfirm: () => {}
      });
      return;
    }

    const nextProductIndex = findNextUnwonProduct();
    if (nextProductIndex === -1) {
      showPopup({
        type: 'info',
        title: 'Thông báo', 
        message: 'Đã quay thưởng hết tất cả sản phẩm!',
        showCancelButton: false,
        onConfirm: () => {}
      });
      return;
    }

    setIsRandomizing(true);
    setShowWheel(true);

    try {
      // Lọc ra những người tham gia không phải creator và chưa trúng thưởng
      const availableParticipants = room.participants.filter(p => {
        return !p.isCreator && 
          !Object.values(productWinners).some(winner => 
            winner.winner === p.name
          );
      });

      if (availableParticipants.length === 0) {
        throw new Error('Không còn người tham gia hợp lệ');
      }

      // Reset position về 0 trước khi bắt đầu quay mới
      setSpinPosition(0);
      
      // Đợi một chút để animation reset hoàn tất
      await new Promise(resolve => setTimeout(resolve, 50));

      // Tạo danh sách items để quay, chỉ từ những người tham gia hợp lệ
      const items = generateSpinItems(availableParticipants);
      const winner = availableParticipants[Math.floor(Math.random() * availableParticipants.length)];
      items.push(winner); // Đảm bảo người thắng ở cuối
      setSpinItems(items);

      // Tính toán vị trí cuối cùng để winner nằm giữa marker
      const finalPosition = -(items.length - 1) * 180 + 400;
      
      // Thêm class để reset animation
      const spinTrack = document.querySelector('.spin-items');
      if (spinTrack) {
        spinTrack.style.transition = 'none';
        spinTrack.offsetHeight; // Force reflow
        spinTrack.style.transition = 'transform 8s cubic-bezier(0.1, 0.7, 0.1, 1)';
      }

      // Bắt đầu quay
      setTimeout(() => {
        setSpinPosition(finalPosition);
        
        // Kết thúc quay sau 8s
        setTimeout(() => {
          finalizePick(winner.name);
        }, 8000);
      }, 100);

    } catch (error) {
      setIsRandomizing(false);
      setShowWheel(false);
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: error.message || 'Có lỗi xảy ra khi chọn người thắng',
        showCancelButton: false,
        onConfirm: () => {}
      });
    }
  };

  useEffect(() => {
    // Redirect to join page if not creator and no password verified
    if (!loading && room && !isCreator && !localStorage.getItem(`room_${roomId}_verified`)) {
      navigate(`/join/${roomId}`);
    }
  }, [loading, room, isCreator, roomId]);

  // Tính toán phân trang
  const filteredParticipants = participants.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const indexOfLastParticipant = currentPage * participantsPerPage;
  const indexOfFirstParticipant = indexOfLastParticipant - participantsPerPage;
  const currentParticipants = filteredParticipants.slice(
    indexOfFirstParticipant,
    indexOfLastParticipant
  );

  // Thêm hàm helper để ẩn email
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const maskedUsername = username.charAt(0) + '*'.repeat(username.length - 2) + username.charAt(username.length - 1);
    return `${maskedUsername}@${domain}`;
  };

  // Thay đổi trong phần render participant
  const renderParticipantEmail = (participant) => {
    if (!isCreator) {
      return maskEmail(participant.email);
    }
    
    return (
      <div className="participant-email">
        {visibleEmails[participant.id] ? participant.email : maskEmail(participant.email)}
        <button
          className="btn btn-link btn-sm ms-2"
          onClick={() => toggleEmailVisibility(participant.id)}
        >
          <i className={`fas fa-${visibleEmails[participant.id] ? 'eye-slash' : 'eye'}`}></i>
        </button>
      </div>
    );
  };

  // Render phần danh sách người tham gia
  const renderParticipants = () => (
    <div className="participants-container">
      <div className="search-box mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Tìm kiếm người tham gia..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <div className="list-group">
        {currentParticipants
          .filter(p => !p.isCreator)
          .map((p, index) => (
            <div 
              key={p._id || index}
              className={`participant-item list-group-item list-group-item-action d-flex align-items-center
                ${isRandomizing && currentRandomName === p.name ? 'highlight' : ''}
                ${room.winner === p.name ? 'winner' : ''}`}
            >
              <i className="fas fa-user me-2"></i>
              <div className="participant-info">
                <div className="participant-name">
                  {isCreator ? (
                    <div className="d-flex align-items-center">
                      {visibleEmails[p._id] ? p.name : maskEmail(p.name)}
                      <button
                        className="btn btn-link btn-sm p-0 ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEmailVisibility(p._id);
                        }}
                        title={visibleEmails[p._id] ? "Ẩn email" : "Hiện email"}
                      >
                        <i className={`fas fa-${visibleEmails[p._id] ? 'eye-slash' : 'eye'}`}></i>
                      </button>
                    </div>
                  ) : (
                    maskEmail(p.name)
                  )}
                </div>
                <small className="text-muted">
                  Tham gia lúc: {new Date(p.joinedAt).toLocaleTimeString()}
                </small>
              </div>
              {room.winner === p.name && (
                <i className="fas fa-trophy text-warning ms-auto me-2"></i>
              )}
              {isCreator && (
                <button
                  className="btn btn-danger btn-sm ms-auto"
                  onClick={() => handleKickParticipant(p.name)}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          ))}
      </div>
    </div>
  );

  // Thêm hàm renderPagination mới
  const renderPagination = () => {
    const totalPages = Math.ceil(filteredParticipants.length / participantsPerPage);
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= totalPages; i++) {
        if (
          i === 1 || 
          i === totalPages ||
          i === currentPage ||
          (currentPage - i >= -delta && currentPage - i <= delta)
        ) {
          range.push(i);
        }
      }

      for (let i of range) {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      }

      return rangeWithDots;
    };

    return (
      <div className="pagination-container">
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {getPageNumbers().map((num, index) => (
            <button
              key={index}
              className={`pagination-btn ${currentPage === num ? 'active' : ''} 
                ${num === '...' ? 'dots' : ''}`}
              onClick={() => num !== '...' && setCurrentPage(num)}
              disabled={num === '...'}
            >
              {num}
            </button>
          ))}

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteRoom = async () => {
    // Kiểm tra xem có phải creator không
    if (!isCreator) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: 'Bạn không có quyền xóa phòng này',
        showCancelButton: false,
        onConfirm: () => {}
      });
      return;
    }

    let confirmed = false; // Thêm biến để theo dõi xác nhận

    await new Promise(resolve => {
      showPopup({
        type: 'warning',
        title: 'Xác nhận xóa phòng',
        message: `Bạn có chắc chắn muốn xóa phòng "${room.name}" không?`,
        showCancelButton: true,
        confirmText: 'Xóa phòng',
        cancelText: 'Hủy',
        onConfirm: () => {
          confirmed = true;
          resolve();
        },
        onCancel: () => {
          confirmed = false;
          resolve();
        }
      });
    });

    // Chỉ thực hiện xóa nếu người dùng đã xác nhận
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { creatorId: room.creator.userId }
        }
      );

      // Xóa các dữ liệu local storage liên quan đến phòng
      localStorage.removeItem(`room_${roomId}_verified`);
      localStorage.removeItem(`room_${roomId}_creator`);

      showPopup({
        type: 'success',
        title: 'Thành công',
        message: 'Đã xóa phòng thành công',
        showCancelButton: false,
        onConfirm: () => navigate('/my-rooms')
      });
    } catch (error) {
      let errorMessage = 'Có lỗi xảy ra khi xóa phòng';
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại';
            setTimeout(() => navigate('/login'), 2000);
            break;
          case 403:
            errorMessage = 'Bạn không có quyền xóa phòng này';
            break;
          case 404:
            errorMessage = 'Phòng không tồn tại hoặc đã bị xóa';
            break;
          default:
            errorMessage = error.response.data?.error || errorMessage;
        }
      }

      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: errorMessage,
        showCancelButton: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}/password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showPopup({
        type: 'success',
        title: 'Thành công',
        message: 'Đã đổi mật khẩu phòng thành công',
        showCancelButton: false
      });
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: error.response?.data?.error || 'Có lỗi xảy ra khi đổi mật khẩu',
        showCancelButton: false
      });
    }
  };

  const handleKickParticipant = async (participantName) => {
    showPopup({
      type: 'warning',
      title: 'Xác nhận kick người tham gia',
      message: `Bạn có chắc chắn muốn kick "${participantName}"?`,
      showCancelButton: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/rooms/${roomId}/participants/${participantName}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          showPopup({
            type: 'success',
            title: 'Thành công',
            message: 'Đã kick người tham gia thành công',
            showCancelButton: false
          });
          // Room sẽ tự động cập nhật qua polling
        } catch (error) {
          showPopup({
            type: 'error',
            title: 'Lỗi',
            message: error.response?.data?.error || 'Có lỗi xảy ra khi kick người tham gia',
            showCancelButton: false
          });
        }
      }
    });
  };

  const handleToggleLock = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}/toggle-lock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Cập nhật state từ response API
      setIsLocked(response.data.isLocked);
      
      showPopup({
        type: 'success',
        title: 'Thành công',
        message: response.data.message,
        showCancelButton: false
      });
    } catch (error) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: error.response?.data?.error || 'Có lỗi xảy ra khi thay đổi trạng thái phòng',
        showCancelButton: false
      });
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}/settings`,
        { maxParticipants },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoom(prev => ({ ...prev, maxParticipants }));
      showPopup({
        type: 'success',
        title: 'Thành công',
        message: 'Đã cập nhật cài đặt phòng',
        showCancelButton: false
      });
      setShowSettingsModal(false);
    } catch (error) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: error.response?.data?.error || 'Có lỗi xảy ra khi cập nhật cài đặt',
        showCancelButton: false
      });
    }
  };

  const handleAddProduct = () => {
    if (newProduct.trim()) {
      setProducts([...products, newProduct.trim()]);
      setNewProduct('');
    }
  };

  const handleRemoveProduct = (index) => {
    const newProducts = [...products];
    newProducts.splice(index, 1);
    setProducts(newProducts);
  };

  const handleSaveProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/rooms/${roomId}/products`,
        { products },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowProductsModal(false);
      showPopup({
        type: 'success',
        title: 'Thành công',
        message: 'Đã cập nhật danh sách sản phẩm',
        showCancelButton: false
      });
    } catch (error) {
      showPopup({
        type: 'error',
        title: 'Lỗi',
        message: 'Không thể cập nhật danh sách sản phẩm',
        showCancelButton: false
      });
    }
  };

  // Thêm các nút quản lý phòng cho creator
  const renderRoomManagement = () => {
    if (!isCreator) return null;
    
    return (
      <div className="room-management-buttons">
        <button 
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShowShareModal(true)}
        >
          <i className="fas fa-share-alt me-2"></i>
          Chia sẻ
        </button>
        <button 
          className={`btn btn-outline-${isLocked ? 'danger' : 'success'} btn-sm`}
          onClick={handleToggleLock}
        >
          <i className={`fas fa-${isLocked ? 'lock' : 'lock-open'} me-2`}></i>
          {isLocked ? 'Mở khóa phòng' : 'Khóa phòng'}
        </button>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShowProductsModal(true)}
        >
          <i className="fas fa-gift me-2"></i>
          Quản lý sản phẩm
        </button>
        <button 
          className="btn btn-outline-info btn-sm"
          onClick={() => setShowSettingsModal(true)}
        >
          <i className="fas fa-cog me-2"></i>
          Cài đặt
        </button>
        <button 
          className="btn btn-outline-warning btn-sm"
          onClick={() => setShowPasswordModal(true)}
        >
          <i className="fas fa-key me-2"></i>
          Đổi mật khẩu
        </button>
        <button 
          className="btn btn-outline-danger btn-sm"
          onClick={handleDeleteRoom}
        >
          <i className="fas fa-trash me-2"></i>
          Xóa phòng
        </button>
      </div>
    );
  };

  // Thêm modal đổi mật khẩu
  const renderPasswordModal = () => (
    <div className={`modal ${showPasswordModal ? 'd-block' : ''}`} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Đổi mật khẩu phòng</h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={() => setShowPasswordModal(false)}
            ></button>
          </div>
          <form onSubmit={handleChangePassword}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Mật khẩu mới</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Hủy
              </button>
              <button type="submit" className="btn btn-primary">
                Lưu thay đổi
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );

  // Thêm modal chia sẻ
  const renderShareModal = () => {
    const shareUrl = `${window.location.origin}/join/${roomId}`;
    
    return (
      <div className={`modal ${showShareModal ? 'd-block' : ''}`} tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Chia sẻ phòng</h5>
              <button 
                type="button" 
                className="btn-close"
                onClick={() => setShowShareModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-4">
                <label className="form-label">Link tham gia</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={shareUrl}
                    readOnly
                  />
                  <button
                    className="btn btn-outline-primary"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        setShowShareModal(false);
                        
                        setTimeout(() => {
                          showPopup({
                            type: 'success',
                            title: 'Thành công',
                            message: 'Đã sao chép link vào clipboard!',
                            showCancelButton: false
                          });
                        }, 300);
                      } catch (error) {
                        showPopup({
                          type: 'error',
                          title: 'Lỗi',
                          message: 'Không thể sao chép link vào clipboard',
                          showCancelButton: false
                        });
                      }
                    }}
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              </div>

              <div className="text-center">
                <div className="qr-code-container mb-3">
                  <QRCodeSVG 
                    value={shareUrl}
                    size={200}
                    level="H"
                    includeMargin={true}
                    imageSettings={{
                      src: "/logo192.png",
                      x: undefined,
                      y: undefined,
                      height: 40,
                      width: 40,
                      excavate: true,
                    }}
                  />
                </div>
                <p className="text-muted small">
                  <i className="fas fa-info-circle me-1"></i>
                  Quét mã QR để tham gia phòng
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </div>
    );
  };

  // Thêm modal cài đặt
  const renderSettingsModal = () => (
    <div className={`modal ${showSettingsModal ? 'd-block' : ''}`} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Cài đặt phòng</h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={() => setShowSettingsModal(false)}
            ></button>
          </div>
          <form onSubmit={handleUpdateSettings}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Số lượng người tham gia tối đa</label>
                <input
                  type="number"
                  className="form-control"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                  min="1"
                  max="1000"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowSettingsModal(false)}
              >
                Hủy
              </button>
              <button type="submit" className="btn btn-primary">
                Lưu thay đổi
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );

  const renderProductsModal = () => (
    <div className={`modal ${showProductsModal ? 'd-block' : ''}`} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Quản lý sản phẩm</h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={() => setShowProductsModal(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Thêm sản phẩm mới</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={newProduct}
                  onChange={(e) => setNewProduct(e.target.value)}
                  placeholder="Nhập tên sản phẩm..."
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={handleAddProduct}
                  disabled={!newProduct.trim()}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
            
            <div className="product-list">
              {products.length === 0 ? (
                <div className="text-muted text-center py-3">
                  Chưa có sản phẩm nào
                </div>
              ) : (
                <div className="list-group">
                  {products.map((product, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      {product}
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemoveProduct(index)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={() => setShowProductsModal(false)}
            >
              Hủy
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSaveProducts}
            >
              Lưu thay đổi
            </button>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </div>
  );

  const renderWinnersList = () => {
    if (Object.keys(productWinners).length === 0) return null;

    return (
      <div className="winners-list mt-4">
        <h5 className="mb-3">
          <i className="fas fa-history me-2"></i>
          Lịch sử trúng thưởng
        </h5>
        <div className="list-group">
          {products.map((product, index) => (
            <div key={index} className="list-group-item">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <span className="winner-number me-3">{index + 1}</span>
                  <div>
                    <div className="product-name mb-1">
                      <i className="fas fa-gift me-2 text-primary"></i>
                      <strong>{product}</strong>
                    </div>
                    {productWinners[product] && (
                      <div className="winner-info">
                        <i className="fas fa-user me-2 text-success"></i>
                        <div className="d-flex align-items-center">
                          {isCreator ? (
                            <>
                              <span className="winner-name">
                                {visibleEmails[`winner_${index}`] ? 
                                  productWinners[product].winner : 
                                  maskEmail(productWinners[product].winner)}
                              </span>
                              <button
                                className="email-toggle-btn ms-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleEmailVisibility(`winner_${index}`);
                                }}
                                title={visibleEmails[`winner_${index}`] ? "Ẩn email" : "Hiện email"}
                              >
                                <i className={`fas fa-${visibleEmails[`winner_${index}`] ? 'eye-slash' : 'eye'}`}></i>
                              </button>
                            </>
                          ) : (
                            <span className="winner-name">
                              {maskEmail(productWinners[product].winner)}
                            </span>
                          )}
                        </div>
                        <span className="winner-timestamp ms-2 text-muted">
                          {new Date(productWinners[product].timestamp).toLocaleTimeString('vi-VN')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {!productWinners[product] ? (
                    <span className="badge bg-secondary">
                      <i className="fas fa-clock me-1"></i>
                      Chưa quay
                    </span>
                  ) : (
                    <span className="badge bg-success">
                      <i className="fas fa-check me-1"></i>
                      Đã trúng
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Thêm thống kê tổng quan */}
        <div className="winners-stats mt-3">
          <div className="row g-2">
            <div className="col-md-4">
              <div className="stats-card">
                <i className="fas fa-gift text-primary"></i>
                <div className="stats-info">
                  <h6>Tổng phần thưởng</h6>
                  <strong>{products.length}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stats-card">
                <i className="fas fa-check-circle text-success"></i>
                <div className="stats-info">
                  <h6>Đã quay</h6>
                  <strong>{Object.keys(productWinners).length}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stats-card">
                <i className="fas fa-clock text-warning"></i>
                <div className="stats-info">
                  <h6>Còn lại</h6>
                  <strong>{products.length - Object.keys(productWinners).length}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFinalResults = () => {
    if (!isCompleted && !winners.length) return null;

    return (
      <div className="winners-section mt-4">
        <div className="winners-header">
          <div className="winners-title">
            <i className="fas fa-trophy text-warning"></i>
            <h3>Kết quả quay thưởng</h3>
          </div>
          {isCompleted && (
            <div className="completed-badge">
              <i className="fas fa-check-circle"></i>
              Đã hoàn thành
            </div>
          )}
        </div>

        <div className="winners-grid">
          {winners.map((winner, index) => (
            <div key={index} className="winner-card">
              <div className="winner-card-header">
                <span className="prize-number">#{index + 1}</span>
                <h4 className="prize-name">{winner.product}</h4>
              </div>
              
              <div className="winner-card-body">
                <div className="winner-info">
                  <i className="fas fa-user-circle"></i>
                  {isCreator ? (
                    <div className="d-flex align-items-center">
                      <span className="winner-name">
                        {visibleEmails[`final_winner_${index}`] ? 
                          winner.winner : 
                          maskEmail(winner.winner)}
                      </span>
                      <button
                        className="email-toggle-btn ms-2"
                        onClick={() => toggleEmailVisibility(`final_winner_${index}`)}
                        title={visibleEmails[`final_winner_${index}`] ? "Ẩn email" : "Hiện email"}
                      >
                        <i className={`fas fa-${visibleEmails[`final_winner_${index}`] ? 'eye-slash' : 'eye'}`}></i>
                      </button>
                    </div>
                  ) : (
                    <span className="winner-name">{maskEmail(winner.winner)}</span>
                  )}
                </div>
                <div className="winner-timestamp">
                  <i className="far fa-clock"></i>
                  <span>{new Date(winner.timestamp).toLocaleString('vi-VN')}</span>
                </div>
              </div>

              <div className="winner-card-footer">
                <div className="confetti-left">
                  <i className="fas fa-star"></i>
                </div>
                <div className="trophy-icon">
                  <i className="fas fa-trophy"></i>
                </div>
                <div className="confetti-right">
                  <i className="fas fa-star"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) return <Loading />;
  if (!room) return <div>Phòng không tồn tại</div>;

  return (
    <div className="room-container py-4">
      <div className="card shadow-lg">
        <div className="room-header">
          <div className="room-title">
            <h2 className="mb-0">{room.name}</h2>
            <p className="mb-0 opacity-75">Mã phòng: {roomId}</p>
          </div>
          {isCreator && renderRoomManagement()}
        </div>

        <div className="card-body">
          {/* Thống kê phòng */}
          <div className="room-stats">
            <div className="stat-card">
              <i className="fas fa-users mb-2"></i>
              <h4>{room.participants.filter(p => !p.isCreator).length} / {room.maxParticipants}</h4>
              <p className="text-muted mb-0">Người tham gia</p>
            </div>
            <div className="stat-card">
              <i className="fas fa-clock mb-2"></i>
              <h4>
                {room.createdAt ? new Date(room.createdAt).toLocaleDateString('vi-VN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </h4>
              <p className="text-muted mb-0">Thời gian tạo</p>
            </div>
            {room.winner && (
              <div className="stat-card">
                <i className="fas fa-trophy mb-2 text-warning"></i>
                <h4>{room.winner}</h4>
                <p className="text-muted mb-0">Người thắng cuộc</p>
              </div>
            )}
            {products.length > 0 && (
              <div className="stat-card">
                <i className="fas fa-gift mb-2"></i>
                <h4>{products.length}</h4>
                <p className="text-muted mb-0">Sản phẩm</p>
              </div>
            )}
          </div>

          {/* Phần còn lại của component */}
          {renderParticipants()}
          {renderPagination()}
          {showWheel && (
            <div className="wheel-container">
              <div className="wheel-content">
                <div className="spin-track">
                  <div 
                    className="spin-items"
                    style={{ transform: `translateX(${spinPosition}px)` }}
                  >
                    {spinItems.map((participant, index) => (
                      <div 
                        key={index} 
                        className={`spin-item participant`}
                      >
                        <div className="participant-avatar">
                          <i className="fas fa-user"></i>
                        </div>
                        <div className="participant-name">{participant.name}</div>
                      </div>
                    ))}
                  </div>
                  <div className="spin-marker"></div>
                </div>
              </div>
            </div>
          )}

          {renderWinnersList()}

          {/* Thêm banner khi phòng đã hoàn thành */}
          {isCompleted && (
            <div className="alert alert-success text-center mb-4 mt-4" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              Phòng đã hoàn thành quay thưởng!
            </div>
          )}

          {/* Thêm nút quay thưởng chỉ khi phòng chưa hoàn thành */}
          {isCreator && products.length > 0 && findNextUnwonProduct() !== -1 && !isCompleted ? (
            <button 
              className="btn btn-primary btn-lg w-100 shadow-lg pick-winner-btn"
              onClick={handlePickWinner}
              disabled={isRandomizing}
            >
              {isRandomizing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Đang quay số ngẫu nhiên...
                </>
              ) : (
                <>
                  <i className="fas fa-gift me-2"></i>
                  Quay thưởng {products[findNextUnwonProduct()]}
                  <small className="ms-2">
                    ({findNextUnwonProduct() + 1}/{products.length})
                  </small>
                </>
              )}
            </button>
          ) : null}

          {/* Hiển thị kết quả cuối cùng */}
          {renderFinalResults()}
        </div>
      </div>

      {renderShareModal()}
      {renderSettingsModal()}
      {renderPasswordModal()}
      {renderProductsModal()}
    </div>
  );
}

export default Room; 